/* eslint-disable import/prefer-default-export */
import * as events from '../events';
import genericEvents from './generic-events';
import { INTEGRATOR_REQUEST_SUBSCRIBE, INTEGRATOR_REQUEST_USER_CANCELLED } from '../integrator_requests';

export const FlowModule = {
    name: 'External MTN',
    logger: {},
    dispatcher: {},
    uiControl: {},
    tracker: {},
    integrator: {},
    productUrl: '',
    locale: '',
    identity: '',
    productSlug: '',
    plan: '',
    fraudDetection: '',
    integratorResponseCode: 200,
    step: '',
    flowSteps: {
        INIT: 'init',
        INIT_ERROR: 'init_error',
        REDIRECTING: 'redirecting',
        SUBSCRIPTION_FAILED: 'subscription_failed',
        SUBSCRIPTION_SUCCEEDED: 'subscription_success',
        CANCELLED: 'cancelled',
        BAD_INTEGRATOR_REQUEST: 'bad_integrator_request',
    },
    pageStates: {
        SUBSCRIBE: 'subscribe',
        CONFIRM: 'confirm',
        ERROR: 'error',
        SUCCESS: 'success',
    },

    /**
     * Method to initialise the flow. This will be binding all the event listeners
     * needed to implement the external flow
     */
    init() {
        this.setFlowStep(this.flowSteps.INIT);

        // Check if it is an integrator request (redirect)
        if (this.integrator.isRedirectRequest()) {
            const integratorRequest = this.integrator.parseRedirectRequest();

            if (integratorRequest) {
                this.bindTrackingEvents();

                if (integratorRequest.isOfType(INTEGRATOR_REQUEST_SUBSCRIBE)) {
                    if (integratorRequest.isSuccess()) {
                        this.setFlowStep(this.flowSteps.SUBSCRIPTION_SUCCEEDED);
                        this.logger.debug(`Flow (${this.name}):integrator requested subscribe success`, { request: integratorRequest });
                        this.dispatcher.dispatchEvent(events.SUBSCRIPTION_SUCCEEDED, {
                            jwt: integratorRequest.jwt,
                            reference: integratorRequest.reference,
                        });
                    } else {
                        this.setFlowStep(this.flowSteps.SUBSCRIPTION_FAILED);
                        this.logger.debug(`Flow (${this.name}): integrator requested subscribe error`, { request: integratorRequest });
                        this.dispatcher.dispatchEvent(events.SUBSCRIPTION_FAILED, {
                            reference: integratorRequest.reference,
                        });
                        this.uiControl.showErrorMessage('INTEGRATOR_ERROR');
                    }
                } else if (integratorRequest.isOfType(INTEGRATOR_REQUEST_USER_CANCELLED)) {
                    this.setFlowStep(this.flowSteps.CANCELLED);
                    // todo: do required tracking
                    this.logger.debug(`Flow (${this.name}): integrator requested subscribe cancelled by user`, { request: integratorRequest });
                    this.dispatcher.dispatchEvent(events.SUBSCRIPTION_CANCELLED, {
                        reference: integratorRequest.reference,
                    });
                    if (integratorRequest.hasRedirect()) {
                        this.dispatcher.dispatchEvent(events.EXIT_CLICK, {
                            reference: integratorRequest.reference,
                        });
                        window.location.replace(integratorRequest.redirect.url);
                    } else {
                        // If no redirect required, we re-display the landing page
                        this.initNewFlow();
                    }
                } else {
                    this.setFlowStep(this.flowSteps.BAD_INTEGRATOR_REQUEST);
                    this.logger.error('Invalid integrator request', { request: integratorRequest });
                    this.initNewFlow();
                }
            } else {
                this.setFlowStep(this.flowSteps.BAD_INTEGRATOR_REQUEST);
                this.initNewFlow();
            }
        } else {
            this.initNewFlow();
        }
    },

    initNewFlow() {
        this.logger.debug(`Flow (${this.name}): initialising flow`);
        this.bindClickEvents();

        if (this.integratorResponseCode === 200) {
            this.logger.debug(`Flow (${this.name}): display first button`);
            this.uiControl.showElement(this.uiControl.controls.external);
            this.tracker.track(events.SHOWN_SUBSCRIBE_BUTTON);
        } else {
            this.setFlowStep(this.flowSteps.INIT_ERROR);
            this.logger.error(`Flow (${this.name}): Integrator error.`, this.integratorResponseCode);
            this.uiControl.showErrorMessage('INTEGRATOR_ERROR');
            this.tracker.track(events.INTEGRATOR_ERROR);
        }
    },

    /**
     * Binding the click events on the button (#flow-subscribe)
     */
    bindClickEvents() {
        this.logger.debug(`Flow (${this.name}): binding click events`);
        genericEvents.bindFlowExitEvent(this);

        this.dispatcher.addEventListener(events.OPEN_EXTERNAL_CLICK, () => {
            this.setFlowStep(this.flowSteps.REDIRECTING);
            this.logger.debug(`Flow (${this.name}): handling button click - go to external LP`);
            this.tracker.track(events.OPEN_EXTERNAL_CLICK);
            this.uiControl.hideElement(this.uiControl.controls.external);
            this.uiControl.showElement(this.uiControl.controls.spinner);

            this.dispatcher.dispatchEvent(events.PRE_SUBSCRIBE);
            // remove unload message
            window.onbeforeunload = null;
            window.location.assign(this.uiControl.controls.external.dataset.lpUrl);
        });
    },

    /**
     * Binding the tracking events
     */
    bindTrackingEvents() {
        this.dispatcher.addEventListener(events.EXIT_CLICK, (event) => {
            this.tracker.track(events.EXIT_CLICK, {
                data: {
                    reference: event.reference,
                },
            });
        });

        this.dispatcher.addEventListener(events.SUBSCRIPTION_FAILED, (event) => {
            this.tracker.track(events.SUBSCRIPTION_FAILED, {
                data: {
                    reference: event.reference,
                },
            });
        });

        this.dispatcher.addEventListener(events.SUBSCRIPTION_CANCELLED, (event) => {
            this.tracker.track(events.SUBSCRIPTION_CANCELLED, {
                data: {
                    reference: event.reference,
                },
            });
        });

        this.dispatcher.addEventListener(events.SUBSCRIPTION_SUCCEEDED, (event) => {
            this.tracker.track(events.SUBSCRIPTION_SUCCEEDED, {
                data: {
                    subscription: event.jwt,
                    reference: event.reference,
                },
            });

            this.redirectToProduct(event.jwt);
        });
    },

    /**
     * Redirect to product with an auth token
     *
     * @param {string} jwt
     */
    redirectToProduct(jwt) {
        // remove unload message
        window.onbeforeunload = null;

        this.tracker.track(events.REDIRECT_TO_PRODUCT);

        let redirectUrl = this.productUrl;
        const queryConcat = (redirectUrl.indexOf('?') !== -1) ? '&' : '?';

        redirectUrl = `${this.productUrl}${queryConcat}auth=${jwt}&locale=${this.locale}`;

        this.logger.debug(`Flow (${this.name}): redirecting to product`, redirectUrl);

        window.location.assign(redirectUrl);
    },

    /**
     * Set the current step of the flow
     *
     * @param {string} flowStep
     */
    setFlowStep(flowStep) {
        this.logger.debug(`Flow (${this.name}): changing step from ${this.step} to ${flowStep}`);
        this.step = flowStep;
        this.setPageStateForFlowStep(flowStep);
    },

    /**
     * Set the current state of the page
     *
     * @param {string} flowStep
     */
    setPageStateForFlowStep(flowStep) {
        switch (flowStep) {
        case this.flowSteps.INIT:
            this.uiControl.setPageState(this.pageStates.SUBSCRIBE);
            break;
        case this.flowSteps.REDIRECTING:
            this.uiControl.setPageState(this.pageStates.CONFIRM);
            break;
        case this.flowSteps.SUBSCRIPTION_SUCCEEDED:
            this.uiControl.setPageState(this.pageStates.SUCCESS);
            break;
        case this.flowSteps.INIT_ERROR:
        case this.flowSteps.SUBSCRIPTION_FAILED:
        case this.flowSteps.CANCELLED:
        case this.flowSteps.BAD_INTEGRATOR_REQUEST:
        default:
            this.uiControl.setPageState(this.pageStates.ERROR);
            break;
        }
    },
};

window.FlowModule = FlowModule;
